"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    AMENDES,
    ECO_ORGANISMES,
    PPWR,
    concerne,
    diagnostic,
    recapitulatif,
} from "@/lib/tools/packagingEpr";
import {
    Avancer,
    Choix,
    Ecran,
    Liste,
    Recommencer,
    Resultat,
    Segments,
    euros,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";

/**
 * La REP emballages : de quels emballages répondez-vous, et êtes-vous en règle ?
 *
 * LE TRI EST LA MOITIÉ DE L'OUTIL. « Producteur » ne veut pas dire fabricant :
 * vendre sous sa marque suffit, importer suffit, et expédier un colis suffit
 * pour le carton. À l'inverse, revendre en boutique une marque déjà vendue en
 * France n'engage rien. Ces quatre cas se ressemblent, et aucun contenu grand
 * public ne les distingue.
 */

const L = {
    fr: {
        phases: ["Ce que vous vendez", "Votre situation", "Diagnostic"],
        retour: "Retour",
        prive: "Tout se fait sur votre appareil : personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",

        clientsTitre: "Qui achète vos produits ?",
        clientsAide: "La filière des emballages ménagers couvre les emballages de ce que des particuliers achètent pour eux-mêmes. Les emballages entre professionnels relèvent d'une autre filière, et d'un autre calendrier.",
        clientsParticuliers: "Des particuliers",
        clientsProfessionnels: "Des entreprises seulement",
        clientsDeux: "Les deux",

        produitsTitre: "Que vendez-vous exactement ?",
        produitsAide: "C'est cette réponse qui décide si l'emballage de vos produits est le vôtre au sens de la loi.",
        produitsMarque: "Mes produits, sous ma marque",
        produitsMarqueDetail: "Que je fabrique ou fasse fabriquer",
        produitsImport: "Des produits que j'importe",
        produitsImportDetail: "Achetés hors de France pour les revendre ici",
        produitsRevente: "Des produits d'autres marques",
        produitsReventeDetail: "Déjà vendus en France par leur fabricant",
        produitsAucun: "Aucun produit physique",
        produitsAucunDetail: "Services, fichiers, prestations",

        expedieTitre: "Comment vos produits arrivent-ils chez le client ?",
        expedieAide: "Le carton, le calage et l'adhésif d'un colis sont des emballages de service. Vous en répondez, même quand le produit dedans est la marque d'un autre.",
        champExpedie: "J'expédie des colis",
        champPlaceDeMarche: "Je vends uniquement via une place de marché qui déclare la REP pour moi",
        oui: "Oui",
        non: "Non",

        horsSujetTitre: "Cette filière ne vous concerne pas",
        refusMenages: "Les emballages ménagers sont ceux dont le déchet finit chez un particulier. Si vos clients sont uniquement des entreprises, vos emballages relèvent de la filière des emballages professionnels, dont le cadre s'applique depuis le 1er juillet 2026 et dont la mise en œuvre opérationnelle démarre le 1er janvier 2027, avec des obligations qui ne sont pas les mêmes. Renseignez-vous auprès d'un éco-organisme de cette filière.",
        refusAucunProduit: "Une prestation, un fichier ou une formation ne partent jamais dans un carton : vous ne mettez aucun emballage sur le marché, et cette filière n'a rien à vous demander. Si vous vous mettez un jour à expédier quoi que ce soit, même un goodies, revenez : le colis suffit à vous faire entrer dans la filière.",
        refusRevendeur: "Vous revendez des produits déjà mis sur le marché français par leur fabricant ou leur importateur : c'est lui le producteur au sens de la loi, et c'est lui qui paie l'éco-contribution de ces emballages. Comme vous n'expédiez pas de colis, vous n'ajoutez aucun emballage de votre côté. Attention si cela change : le jour où vous expédiez, le carton devient le vôtre.",
        refusPlaceDeMarche: "Depuis le 1er janvier 2022, la place de marché est tenue d'assumer la responsabilité élargie pour ses vendeurs tiers (article L. 541-10-9 du code de l'environnement), sauf s'ils prouvent qu'ils s'en acquittent déjà. Vérifiez-le noir sur blanc dans vos conditions de vendeur, et gardez-en la trace : si elle ne le fait pas, l'obligation vous revient entièrement, identifiant unique compris.",

        etatTitre: "Où en êtes-vous ?",
        etatAide: "Quatre obligations se suivent dans cet ordre : adhérer, obtenir l'identifiant, l'afficher, et marquer vos emballages.",
        champEcoOrganisme: "J'adhère à un éco-organisme ({liste})",
        champIdentifiant: "J'ai reçu mon identifiant unique",
        champAfficheIdentifiant: "Il figure sur mon site et dans mes conditions générales",
        champSignaletique: "Mes emballages portent le Triman et les consignes de tri",
        champForme: "Mon entreprise est",
        formeIndividuelle: "Une entreprise individuelle",
        formeSociete: "Une société",

        continuer: "Continuer",
        voirDiagnostic: "Voir mon diagnostic",
        resultatTitre: "Votre situation",
        recapTitre: "Ce que vous avez déclaré",
        recommencer: "Recommencer",

        enRegleTitre: "Vous êtes en règle",
        enRegleTexte: "Vous adhérez, votre identifiant est affiché et vos emballages sont marqués. Il reste à déclarer chaque année les quantités mises sur le marché, à la date fixée par votre éco-organisme : c'est cette déclaration qui fait vivre l'adhésion.",
        manquesTitre: "Il vous manque {n} chose",
        manquesTitrePluriel: "Il vous manque {n} choses",
        manquesAide: "Le défaut d'identifiant unique est puni d'une amende pouvant aller jusqu'à {amendeIdu}. L'absence de signalétique de tri coûte {amendeSignaletique}.",
        manqueAdherer: "Adhérer à un éco-organisme agréé : c'est lui qui vous enregistre et fait délivrer votre identifiant.",
        manqueIdentifiant: "Obtenir votre identifiant unique, qui atteste de votre enregistrement.",
        manqueAfficher: "Afficher cet identifiant sur votre site internet et dans vos conditions générales de vente.",
        manqueSignaletique: "Apposer le Triman et les consignes de tri sur vos emballages.",

        responsabilitesTitre: "Ce dont vous répondez",
        respProduit: "L'emballage de vos produits, parce que vous les mettez sur le marché sous votre marque ou que vous les importez.",
        respExpedition: "Vos emballages d'expédition, carton, calage et adhésif compris, parce que vous constituez le colis.",

        suiteTitre: "Et maintenant",
        suite1: "Choisissez un éco-organisme agréé pour la filière : {liste}. Les petits volumes ont droit à une contribution et à une déclaration simplifiées.",
        suite2: "Attendez votre identifiant unique, puis écrivez-le sur votre site et dans vos conditions générales : c'est lui qu'un contrôle cherche en premier.",
        suite3: "Déclarez chaque année les quantités mises sur le marché, avant la date que votre éco-organisme vous donnera.",
        suite4: "Vérifiez la signalétique de vos emballages : le Triman et les consignes de tri sont obligatoires sur ce que vous emballez vous-même.",

        fondTitre: "Le calendrier européen",
        fond: "Le règlement européen PPWR s'applique depuis le {depuis}, mais ses critères de recyclabilité et ses règles de réemploi n'arrivent qu'en {gros}. Il ne remplace pas les obligations françaises ci-dessus, qui s'appliquent toujours sans changement.",
    },
    en: {
        phases: ["What you sell", "Your situation", "Diagnosis"],
        retour: "Back",
        prive: "Everything happens on your device: nobody receives your answers, not even us, and it all disappears with the tab.",

        clientsTitre: "Who buys your products?",
        clientsAide: "The household packaging scheme covers the packaging of what private individuals buy for themselves. Packaging between businesses comes under a different scheme, with a different timetable.",
        clientsParticuliers: "Individuals",
        clientsProfessionnels: "Companies only",
        clientsDeux: "Both",

        produitsTitre: "What exactly do you sell?",
        produitsAide: "This answer decides whether your products' packaging is yours in the eyes of the law.",
        produitsMarque: "My own products, under my brand",
        produitsMarqueDetail: "Whether I make them or have them made",
        produitsImport: "Products I import",
        produitsImportDetail: "Bought outside France to resell here",
        produitsRevente: "Other brands' products",
        produitsReventeDetail: "Already sold in France by their maker",
        produitsAucun: "No physical products",
        produitsAucunDetail: "Services, files, consulting",

        expedieTitre: "How do your products reach the customer?",
        expedieAide: "The cardboard, padding and tape of a parcel are service packaging. You answer for them, even when the product inside is somebody else's brand.",
        champExpedie: "I ship parcels",
        champPlaceDeMarche: "I only sell through a marketplace that handles the scheme for me",
        oui: "Yes",
        non: "No",

        horsSujetTitre: "This scheme does not concern you",
        refusMenages: "Household packaging is packaging whose waste ends up with a private individual. If your customers are only businesses, your packaging comes under the professional packaging scheme, whose framework has applied since 1 July 2026 and which becomes fully operational on 1 January 2027, with different obligations. Talk to the eco-organisation for that scheme.",
        refusAucunProduit: "A service, a file or a training course never goes into a box: you place no packaging on the market, and this scheme has nothing to ask of you. If you ever start shipping anything, even merchandise, come back: the parcel alone brings you into the scheme.",
        refusRevendeur: "You resell products already placed on the French market by their maker or importer: they are the producer in the eyes of the law, and they pay the eco-contribution on that packaging. Since you ship no parcels, you add no packaging of your own. Watch out if that changes: the day you ship, the cardboard becomes yours.",
        refusPlaceDeMarche: "Since 1 January 2022, a marketplace has been required to take on producer responsibility for its third-party sellers (article L. 541-10-9 of the environment code), unless they prove they already meet it. Check it in writing in your seller terms and keep the evidence: if it does not, the obligation falls back on you entirely, unique identifier included.",

        etatTitre: "Where do you stand?",
        etatAide: "Four obligations follow in this order: join, obtain the identifier, display it, and mark your packaging.",
        champEcoOrganisme: "I belong to an eco-organisation ({liste})",
        champIdentifiant: "I have received my unique identifier",
        champAfficheIdentifiant: "It appears on my site and in my terms and conditions",
        champSignaletique: "My packaging carries the Triman logo and sorting instructions",
        champForme: "My business is",
        formeIndividuelle: "A sole trader",
        formeSociete: "A company",

        continuer: "Continue",
        voirDiagnostic: "See my diagnosis",
        resultatTitre: "Where you stand",
        recapTitre: "What you told us",
        recommencer: "Start again",

        enRegleTitre: "You are compliant",
        enRegleTexte: "You belong to a scheme, your identifier is displayed and your packaging is marked. What remains is to declare each year the quantities placed on the market, by the date your eco-organisation sets: that declaration is what keeps the membership alive.",
        manquesTitre: "You are missing {n} thing",
        manquesTitrePluriel: "You are missing {n} things",
        manquesAide: "Failing to hold a unique identifier is punished by a fine of up to {amendeIdu}. Missing sorting information costs {amendeSignaletique}.",
        manqueAdherer: "Join an approved eco-organisation: they register you and have your identifier issued.",
        manqueIdentifiant: "Obtain your unique identifier, which evidences your registration.",
        manqueAfficher: "Display that identifier on your website and in your terms and conditions of sale.",
        manqueSignaletique: "Put the Triman logo and sorting instructions on your packaging.",

        responsabilitesTitre: "What you answer for",
        respProduit: "Your products' packaging, because you place them on the market under your brand or you import them.",
        respExpedition: "Your shipping packaging, cardboard, padding and tape included, because you make up the parcel.",

        suiteTitre: "What happens next",
        suite1: "Choose an approved eco-organisation for the scheme: {liste}. Small volumes qualify for a simplified contribution and declaration.",
        suite2: "Wait for your unique identifier, then write it on your site and in your terms and conditions: it is the first thing an inspection looks for.",
        suite3: "Declare each year the quantities placed on the market, by the date your eco-organisation gives you.",
        suite4: "Check your packaging: the Triman logo and sorting instructions are compulsory on what you package yourself.",

        fondTitre: "The European timetable",
        fond: "The EU PPWR regulation has applied since {depuis}, but its recyclability criteria and reuse rules only arrive in {gros}. It does not replace the French obligations above, which still apply unchanged.",
    },
};

const PHASES_PAR_ECRAN = {
    clients: 0,
    produits: 0,
    expedie: 0,
    horsSujet: 0,
    etat: 1,
    resultat: 2,
};

/* Aucune réponse posée d'avance : « non » à la place de marché et « entreprise
   individuelle » répondaient à la place du lecteur, l'une en le gardant dans un
   parcours qui ne le concernait peut-être pas, l'autre en affichant un montant
   d'amende qu'il n'avait pas choisi. */
const VIDE = {
    clients: "",
    produits: "",
    expedie: "",
    placeDeMarche: "",
    forme: "",
    ecoOrganisme: "",
    identifiant: "",
    afficheIdentifiant: "",
    signaletique: "",
};

export default function RepEmballages({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    // Le dossier du lecteur, s'il en a un : la forme juridique part déjà
    // posée. La première question (la clientèle) ne s'en déduit pas : le
    // parcours part de son début.
    const { valeurs: dossier } = useDossierOutil();
    const [r, setR] = useState(() => ({ ...VIDE, ...(dossier.forme ? { forme: dossier.forme } : {}) }));

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "clients",
        () => setR(VIDE),
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    useSignalerDossier({ forme: r.forme }, etape === "resultat");

    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));
    const etat = useMemo(() => diagnostic(r), [r]);
    const recap = useMemo(() => recapitulatif(r), [r]);
    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };
    const tri = concerne(r);
    const liste = ECO_ORGANISMES.join(", ");

    const ouiNon = [
        { cle: "oui", libelle: t.oui },
        { cle: "non", libelle: t.non },
    ];

    if (etape === "clients") {
        return (
            <Ecran key={etape} {...commun} phase={0} titre={t.clientsTitre} aide={t.clientsAide}>
                <div className="grid sm:grid-cols-3 gap-3">
                    {[
                        { cle: "particuliers", icone: "user-rounded-linear", titre: t.clientsParticuliers },
                        { cle: "professionnels", icone: "buildings-2-linear", titre: t.clientsProfessionnels },
                        { cle: "les-deux", icone: "users-group-rounded-linear", titre: t.clientsDeux },
                    ].map((c) => (
                        <Choix
                            key={c.cle}
                            icone={c.icone}
                            titre={c.titre}
                            onClick={() => {
                                setR((p) => ({ ...p, clients: c.cle }));
                                aller(c.cle === "professionnels" ? "horsSujet" : "produits");
                            }}
                        />
                    ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "produits") {
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={t.produitsTitre} aide={t.produitsAide}>
                <div className="grid sm:grid-cols-2 gap-3">
                    {[
                        { cle: "maMarque", icone: "sale-linear", titre: t.produitsMarque, detail: t.produitsMarqueDetail },
                        { cle: "import", icone: "global-linear", titre: t.produitsImport, detail: t.produitsImportDetail },
                        { cle: "revente", icone: "case-round-linear", titre: t.produitsRevente, detail: t.produitsReventeDetail },
                        { cle: "aucun", icone: "close-circle-linear", titre: t.produitsAucun, detail: t.produitsAucunDetail },
                    ].map((c) => (
                        <Choix
                            key={c.cle}
                            icone={c.icone}
                            titre={c.titre}
                            detail={c.detail}
                            onClick={() => {
                                setR((p) => ({ ...p, produits: c.cle }));
                                aller(c.cle === "aucun" ? "horsSujet" : "expedie");
                            }}
                        />
                    ))}
                </div>
            </Ecran>
        );
    }

    if (etape === "expedie") {
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={t.expedieTitre} aide={t.expedieAide}>
                <div className="space-y-5">
                    <div>
                        <Segments libelle={t.champExpedie} options={ouiNon} valeur={r.expedie} poser={poser("expedie")} />
                    </div>
                    <div>
                        <Segments
                            libelle={t.champPlaceDeMarche}
                            options={ouiNon}
                            valeur={r.placeDeMarche}
                            poser={poser("placeDeMarche")}
                        />
                    </div>
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!r.expedie || !r.placeDeMarche}
                    onClick={() => aller(concerne({ ...r }).concerne ? "etat" : "horsSujet")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "horsSujet") {
        const texte = {
            horsMenages: t.refusMenages,
            aucunProduit: t.refusAucunProduit,
            revendeurSansColis: t.refusRevendeur,
            placeDeMarche: t.refusPlaceDeMarche,
        }[tri.motif];
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.horsSujetTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4">
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-0">{texte}</p>
                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            </Resultat>
        );
    }

    if (etape === "etat") {
        const surSaMarque = r.produits === "maMarque" || r.produits === "import";
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.etatTitre} aide={t.etatAide}>
                <div className="space-y-5">
                    {[
                        { cle: "ecoOrganisme", libelle: remplir(t.champEcoOrganisme, { liste }) },
                        { cle: "identifiant", libelle: t.champIdentifiant },
                        { cle: "afficheIdentifiant", libelle: t.champAfficheIdentifiant },
                        ...(surSaMarque ? [{ cle: "signaletique", libelle: t.champSignaletique }] : []),
                    ].map((q) => (
                        <div key={q.cle}>
                            <Segments libelle={q.libelle} options={ouiNon} valeur={r[q.cle]} poser={poser(q.cle)} />
                        </div>
                    ))}
                    <div>
                        <Segments
                            libelle={t.champForme}
                            options={[
                                { cle: "personnePhysique", libelle: t.formeIndividuelle },
                                { cle: "societe", libelle: t.formeSociete },
                            ]}
                            valeur={r.forme}
                            poser={poser("forme")}
                        />
                    </div>
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !r.ecoOrganisme ||
                        !r.identifiant ||
                        !r.afficheIdentifiant ||
                        !r.forme ||
                        (surSaMarque && !r.signaletique)
                    }
                    onClick={() => aller("resultat")}
                >
                    {t.voirDiagnostic}
                </Avancer>
            </Ecran>
        );
    }

    const libelleManque = {
        adherer: t.manqueAdherer,
        identifiant: t.manqueIdentifiant,
        afficherIdentifiant: t.manqueAfficher,
        signaletique: t.manqueSignaletique,
    };
    const libelleResponsabilite = {
        emballageProduit: t.respProduit,
        emballageExpedition: t.respExpedition,
    };

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            <div className="mt-4">
                {etat.enRegle ? (
                    <div className="rounded-2xl border border-green-600/30 bg-green-600/[0.06] p-5 mb-6">
                        <div className="flex items-center gap-2.5 mb-2">
                            <SolarIcon
                                name="check-circle-linear"
                                className="text-xl shrink-0 text-green-700 dark:text-green-400"
                            />
                            <div className="font-bold dark:text-white leading-snug">{t.enRegleTitre}</div>
                        </div>
                        <p className="text-sm leading-7 text-gray-700 dark:text-lightBlue mb-0">
                            {t.enRegleTexte}
                        </p>
                    </div>
                ) : (
                    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-5 mb-6">
                        <div className="flex items-center gap-2.5 mb-2">
                            <SolarIcon
                                name="danger-triangle-linear"
                                className="text-xl shrink-0 text-amber-600 dark:text-amber-400"
                            />
                            <div className="font-bold dark:text-white leading-snug">
                                {remplir(etat.manques.length > 1 ? t.manquesTitrePluriel : t.manquesTitre, {
                                    n: etat.manques.length,
                                })}
                            </div>
                        </div>
                        <p className="text-sm leading-7 text-gray-700 dark:text-lightBlue mb-0">
                            {remplir(t.manquesAide, {
                                amendeIdu: euros(AMENDES.identifiant, lang),
                                amendeSignaletique: euros(etat.amendeSignaletique, lang),
                            })}
                        </p>
                        <Liste items={etat.manques.map((m) => libelleManque[m])} />
                    </div>
                )}

                <div className="mb-6">
                    <div className="flex items-center gap-2.5 mb-1">
                        <SolarIcon
                            name="checklist-minimalistic-linear"
                            className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                        />
                        <div className="font-bold dark:text-white leading-snug">{t.responsabilitesTitre}</div>
                    </div>
                    <Liste items={etat.responsabilites.map((c) => libelleResponsabilite[c])} />
                </div>

                <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                    {t.recapTitre}
                </div>
                <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden mb-6">
                    <table className="w-full text-sm">
                        <tbody>
                            {recap.map(([titre, valeur]) => (
                                <tr
                                    key={titre}
                                    className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10"
                                >
                                    <td className="px-5 py-3 text-gray-500 dark:text-lightBlue/70 align-top w-3/5">
                                        {titre}
                                    </td>
                                    <td className="px-5 py-3 font-medium dark:text-white">{valeur}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-8">
                    <div className="flex items-center gap-2.5 mb-4">
                        <SolarIcon
                            name="clipboard-check-linear"
                            className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                        />
                        <div className="font-bold dark:text-white leading-snug">{t.suiteTitre}</div>
                    </div>
                    <ul className="space-y-3">
                        {[remplir(t.suite1, { liste }), t.suite2, t.suite3, t.suite4].map((ligne) => (
                            <li
                                key={ligne}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/20" />
                                <span>{ligne}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                    <div className="flex items-center gap-2.5 mb-3">
                        <SolarIcon
                            name="global-linear"
                            className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                        />
                        <div className="font-semibold dark:text-white leading-snug">{t.fondTitre}</div>
                    </div>
                    <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                        {remplir(t.fond, {
                            depuis: lang === "en" ? "12 August 2026" : PPWR.applicableDepuis,
                            gros: PPWR.grosDesObligations,
                        })}
                    </p>
                </div>

                <Recommencer onClick={recommencer} libelle={t.recommencer} />
            </div>
        </Resultat>
    );
}
